import { clamp } from 'Lib/utils/lodashUtils';

type CropBox = {
  imageInterestAspectRatio: number;
  isHorizontalTrim: boolean;
  isVerticalTrim: boolean;
  targetAspectRatio?: number;
  targetHeight: number;
  targetLeft: number;
  targetTop: number;
  targetWidth: number;
};

type BackgroundPositioning = {
  positionX: string;
  positionY: string;
  sizeHeight: string;
  sizeWidth: string;
  targetAspectRatio: number;
};

export function getCropBox(
  imageWidth: number,
  imageHeight: number,
  interestLeft: number,
  interestTop: number,
  interestRight: number,
  interestBottom: number,
  displayAspectRatio?: number
): CropBox {
  let targetLeft = 0;
  let targetTop = 0;
  let targetWidth = imageWidth;
  let targetHeight = imageHeight;

  const imageInterestLeft = Math.round(imageWidth * interestLeft);
  const imageInterestTop = Math.round(imageHeight * interestTop);
  const imageInterestWidth = Math.round(imageWidth * (interestRight - interestLeft));
  const imageInterestHeight = Math.round(imageHeight * (interestBottom - interestTop));
  const imageInterestAspectRatio = imageInterestWidth / imageInterestHeight;

  const imageAspectRatio = imageWidth / imageHeight;
  const targetAspectRatio = displayAspectRatio || imageInterestAspectRatio;
  const isHorizontalTrim = targetAspectRatio < imageAspectRatio;
  const isVerticalTrim = targetAspectRatio > imageAspectRatio;

  // Trim from either the left/right or top/bottom
  if (isHorizontalTrim) {
    targetWidth = imageHeight * targetAspectRatio;
    targetLeft = clamp(
      imageInterestLeft + 0.5 * (imageInterestWidth - targetWidth),
      0,
      imageWidth - targetWidth
    );
    targetWidth = Math.round(targetWidth);
    targetLeft = Math.round(targetLeft);
  } else {
    targetHeight = imageWidth / targetAspectRatio;
    targetTop = clamp(
      imageInterestTop + 0.5 * (imageInterestHeight - targetHeight),
      0,
      imageHeight - targetHeight
    );
    targetHeight = Math.round(targetHeight);
    targetTop = Math.round(targetTop);
  }

  return {
    imageInterestAspectRatio,
    isHorizontalTrim,
    isVerticalTrim,
    targetAspectRatio,
    targetHeight,
    targetLeft,
    targetTop,
    targetWidth,
  };
}

export function getZoomBox(
  imageWidth: number,
  imageHeight: number,
  interestLeft: number,
  interestTop: number,
  interestRight: number,
  interestBottom: number,
  displayAspectRatio?: number
): CropBox {
  const imageInterestLeft = Math.round(imageWidth * interestLeft);
  const imageInterestTop = Math.round(imageHeight * interestTop);
  const imageInterestWidth = Math.round(imageWidth * (interestRight - interestLeft));
  const imageInterestHeight = Math.round(imageHeight * (interestBottom - interestTop));
  const imageInterestAspectRatio = imageInterestWidth / imageInterestHeight;
  const imageInterestCenterX = Math.round(0.5 * imageWidth * (interestLeft + interestRight));
  const imageInterestCenterY = Math.round(0.5 * imageHeight * (interestTop + interestBottom));

  let targetLeft = imageInterestLeft;
  let targetTop = imageInterestTop;
  let targetWidth = imageInterestWidth;
  let targetHeight = imageInterestHeight;

  const targetAspectRatio = displayAspectRatio || imageInterestAspectRatio;
  const needsMoreWidth = targetAspectRatio > imageInterestAspectRatio;
  let isHorizontalTrim = false;
  let isVerticalTrim = false;

  if (needsMoreWidth) {
    targetWidth = targetHeight * targetAspectRatio;
    targetLeft = clamp(imageInterestCenterX - 0.5 * targetWidth, 0, imageWidth - targetWidth);
    // If the target width is too large, max-width and center around the focal point vertically
    if (targetWidth > imageWidth) {
      targetWidth = imageWidth;
      targetLeft = 0;
      targetHeight = targetWidth / targetAspectRatio;
      targetTop = clamp(imageInterestCenterY - 0.5 * targetHeight, 0, imageHeight - targetHeight);
      isVerticalTrim = true;
    }
  } else {
    targetHeight = targetWidth / targetAspectRatio;
    targetTop = clamp(imageInterestCenterY - 0.5 * targetHeight, 0, imageHeight - targetHeight);
    // If target height is too large, max-height and center around the focal point horizontally
    if (targetHeight > imageHeight) {
      targetHeight = imageHeight;
      targetTop = 0;
      targetWidth = targetHeight * targetAspectRatio;
      targetLeft = clamp(imageInterestCenterX - 0.5 * targetWidth, 0, imageWidth - targetWidth);
      isHorizontalTrim = true;
    }
  }

  return {
    imageInterestAspectRatio,
    isHorizontalTrim,
    isVerticalTrim,
    targetAspectRatio,
    targetHeight,
    targetLeft,
    targetTop,
    targetWidth,
  };
}

export function getBackgroundPositioning(
  imageWidth: number,
  imageHeight: number,
  targetLeft: number,
  targetTop: number,
  targetWidth: number,
  targetHeight: number
): BackgroundPositioning {
  const positionX =
    imageWidth !== targetWidth ? `${100 * (targetLeft / (imageWidth - targetWidth))}%` : '50%';
  const positionY =
    imageHeight !== targetHeight ? `${100 * (targetTop / (imageHeight - targetHeight))}%` : '50%';
  const sizeWidth = `${100 * (imageWidth / targetWidth)}%`;
  const sizeHeight = `${100 * (imageHeight / targetHeight)}%`;

  const targetAspectRatio = targetWidth / targetHeight;

  return {
    positionX,
    positionY,
    sizeWidth,
    sizeHeight,
    targetAspectRatio,
  };
}
